import { createAsyncThunk } from '@reduxjs/toolkit';

import { AgentType, AgentTypeName, IdType } from '@/enums';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { store } from '@/store';
import { ReminderRequest } from '@/types/Reminder/Reminder';
import { getAPIPath } from '@/utility/common';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const reminder = createAsyncThunk(
  'application/Reminder/SetEmailReminder',
  async ({
    productCode,
    expiryDate,
    policyNo,
    reference,
    email,
    language,
  }: {
    productCode: string;
    expiryDate: string;
    policyNo: string;
    reference: string;
    language: string;
    email: string;
    // marketingConsent: boolean;
    // registrationNumber: string;
  }) => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      const request: ReminderRequest = {
        countryCode: getCountryCode(),
        productCode,
        expiryDate,
        policyNo,
        reference,
        email,
        language,
      };
      const relativeUrl = 'Reminder/SetEmailReminder';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const processVehicleData = createAsyncThunk(
  'quote/ProcessVehicleData',
  async ({
    productCode,
    language,
    entityType,
  }: {
    productCode: string;
    language: string;
    entityType: number;
  }) => {
    try {
      const rootState = store.getState();
      const uiState = rootState.uiState;
      const infoState = rootState.infoState;

      let customer;
      const idType = infoState.personalDetail.idType;
      const idNumber = infoState.personalDetail.idNumber.toString();
      const idNumber2 = infoState.personalDetail.idNumber2?.toString();
      const dob = infoState.personalDetail.dateOfBirth;
      if (idType == IdType.MyKad) {
        customer = {
          identityType: idType,
          identityNumber: idNumber,
        };
      } else if (idType == IdType.Passport) {
        customer = {
          identityType: idType,
          identityNumber: idNumber,
          citizenship: infoState.personalDetail.nationalityDesc,
          countryIsoCode: infoState.personalDetail.nationalityIso,
          birthDate: dob,
        };
      } else {
        customer = {
          identityType: IdType.MyKad,
          identityNumber: idNumber,
          secondaryIdentityType: idType,
          secondaryIdentityNumber: idNumber2,
          birthDate: dob,
        };
      }

      const request = {
        countryCode: getCountryCode(),
        productCode,
        customer,
        vehicle: {
          registrationNo: infoState.vehicleDetail.vehicleNumber,
        },
        entity: entityType,
        language,
      };

      const relativeUrl = `pa/ProcessVehicleData`;
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);

      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const calculatePlanPrice = createAsyncThunk(
  'quote/CalculatePremium',
  async ({
    productCode,
    language,
    planClass,
    entityType,
  }: {
    productCode: string;
    language: string;
    planClass: number;
    entityType: number;
  }) => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      const uiState = rootState.uiState;
      const infoState = rootState.infoState;

      let customer;
      const idType = infoState.personalDetail.idType;
      const idNumber = infoState.personalDetail.idNumber.toString();
      const idNumber2 = infoState.personalDetail.idNumber2?.toString();
      const dob = infoState.personalDetail.dateOfBirth;
      const affiliateState = rootState.affiliate;

      let agentType = AgentType.Default;
      if (affiliateState.type === AgentTypeName.Agent) {
        agentType = affiliateState.isDirectAgent ? AgentType.DirectAgent : AgentType.IndirectAgent;
      } else if (affiliateState.type === AgentTypeName.Banca) {
        agentType = affiliateState.isStaff ? AgentType.BancaStaff : AgentType.BancaNonStaff;
      }
      if (idType === IdType.MyKad) {
        customer = {
          identityType: idType,
          identityNumber: idNumber,
        };
      } else if (idType === IdType.Passport) {
        customer = {
          identityType: idType,
          identityNumber: idNumber,
          citizenship: infoState.personalDetail.nationality,
          countryIsoCode: infoState.personalDetail.nationalityIso,
          birthDate: dob,
        };
      } else {
        customer = {
          identityType: IdType.MyKad,
          identityNumber: idNumber,
          secondaryIdentityType: idType,
          secondaryIdentityNumber: idNumber2,
          birthDate: dob,
        };
      }

      const request = {
        countryCode: getCountryCode(),
        quotationNo: planSummary.processVehicle?.quotationNumber || '',
        productCode,
        customer,
        vehicle: {
          registrationNo: infoState.vehicleDetail.vehicleNumber,
          mmCode: infoState.vehicleDetail.mmCode,
          chassisNo: infoState.vehicleDetail.chassisNumber,
        },
        planClass,
        isEtiqa: planSummary.processVehicle?.isEtiqa,
        hasNvic: planSummary.processVehicle?.hasNvic,
        agentType,
        agentCode: planSummary.agentCode,
        isStaff: false,
        language,
        entity: entityType,
      };

      if (affiliateState.agentCode) {
        request.agentCode = affiliateState.agentCode;
      }

      const relativeUrl = `${getAPIPath()}/CalculatePremium`;
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProduct = createAsyncThunk(
  'productStatus',
  async ({ productCode }: { productCode: string }) => {
    try {
      const relativeUrl = 'product/getstatus/';
      const queryString = productCode;
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);
